import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { addDealNote, listDealNotes, updateDealNote, type Deal, type DealNote } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { NoteBody, NoteEntry, NoteThread } from "@/components/deals/NoteThread";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/toast";

const NOTE_MAX = 2000;

/**
 * Saveable deal notes on the detail page (CHP-340).
 *
 * The owning BDM/PO (or the on-behalf creator) can append notes and edit each
 * for a short window (5 minutes; the server returns `editable` per note once it
 * locks). CM/RA/RM see the same list read-only — no add input, no edit button.
 * Notes are append-only deal history, never compulsory.
 */
export function DealNotesSection({ deal, heading = "Notes" }: { deal: Deal; heading?: string }) {
  const { accessToken, data: user } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isManager = user?.role === "channel_manager" || user?.role === "admin";
  const isOwner = user != null && (user.id === deal.bdm_id || user.id === deal.created_by_id);
  const canAuthor = isOwner && !isManager;

  const notesQuery = useQuery({
    queryKey: ["deals", "notes", deal.id],
    queryFn: () => listDealNotes(accessToken!, deal.id),
    enabled: !!accessToken,
  });

  const [draft, setDraft] = React.useState("");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["deals", "notes", deal.id] });

  const add = useMutation({
    mutationFn: () => addDealNote(accessToken!, deal.id, { body: draft.trim() }),
    onSuccess: () => {
      setDraft("");
      setError(null);
      invalidate();
      toast({ variant: "success", description: "Note saved" });
    },
    onError: () => setError("Couldn't save the note. Refresh and try again."),
  });

  const edit = useMutation({
    mutationFn: (noteId: string) =>
      updateDealNote(accessToken!, deal.id, noteId, { body: editDraft.trim() }),
    onSuccess: () => {
      setEditingId(null);
      setEditDraft("");
      setError(null);
      invalidate();
      toast({ variant: "success", description: "Note updated" });
    },
    onError: () => setError("Couldn't update the note. The edit window may have closed."),
  });

  const notes = notesQuery.data ?? [];

  function startEdit(note: DealNote) {
    setEditingId(note.id);
    setEditDraft(note.body);
    setError(null);
  }

  return (
    <section>
      <h2 className="mb-3 text-base font-semibold">{heading}</h2>

      <NoteThread
        entries={notes}
        isLoading={notesQuery.isLoading}
        isError={notesQuery.isError}
        loadingLabel="Loading notes…"
        emptyLabel="No notes yet."
        errorLabel="Couldn't load notes. Please try again."
        onRetry={() => void notesQuery.refetch()}
        renderEntry={(note) => (
          <NoteEntry
            key={note.id}
            author={note.author_name}
            timestamp={note.created_at}
            edited={note.updated_at !== note.created_at}
            headerAction={
              note.editable && editingId !== note.id ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => startEdit(note)}>
                  Edit
                </Button>
              ) : null
            }
          >
            {editingId === note.id ? (
              <div className="mt-2 space-y-2">
                <Textarea
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  maxLength={NOTE_MAX}
                  rows={3}
                  className="w-full"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (!editDraft.trim()) return;
                      edit.mutate(note.id);
                    }}
                    disabled={!editDraft.trim()}
                    loading={edit.isPending}
                    loadingLabel="Saving…"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditingId(null);
                      setEditDraft("");
                    }}
                    disabled={edit.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <NoteBody>{note.body}</NoteBody>
            )}
          </NoteEntry>
        )}
      />

      {canAuthor ? (
        <div className="mt-4 space-y-2">
          <label className="block text-sm font-medium" htmlFor="deal-note-input">
            Add a note
          </label>
          <Textarea
            id="deal-note-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={NOTE_MAX}
            rows={3}
            placeholder="Add a note to this deal…"
            className="w-full"
            disabled={add.isPending}
          />
          <div>
            <Button
              type="button"
              onClick={() => {
                if (!draft.trim()) return;
                add.mutate();
              }}
              disabled={!draft.trim()}
              loading={add.isPending}
              loadingLabel="Saving…"
            >
              Add note
            </Button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </section>
  );
}
